import React, {useState, useEffect} from 'react';

import {Chromosome, GenomeOption} from 'constants/GenomeConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {chromosomeToName} from 'util/GenomeUtil';
import * as net from 'util/Net';

interface GenomeRegionSelectorProps {
  className?: string;
  testElement?: string;
  genome: GenomeOption | null;
  value: Chromosome | null;
  onChange: (chr: Chromosome) => void;
  chromosomeRegions?: Array<Chromosome>;
}

function GenomeRegionSelector({
  className,
  testElement,
  genome,
  value,
  onChange,
  chromosomeRegions,
}: GenomeRegionSelectorProps): JSX.Element {
  const [chromosomeOptionData, setChromosomeOptionData] = useState<Array<OptionData>>([]);

  useEffect(() => {
    async function setRegions(): Promise<void> {
      // Allow the option to pre-fetch and pass in the chromosomes instead of fetching on mount.
      // For live Ensembl genomes, we don't want to use up calls and hit the external endpoint
      // to fetch the same data for every dropdown
      let chromosomes = chromosomeRegions ?? [];
      if (chromosomes.length === 0 && genome != null) {
        const {results} = await net.getJSONPromise(`/1/api/genomes/${genome.id}/regions`);
        chromosomes = results;
      }
      const optionData = chromosomes.map((chr: Chromosome) => ({
        name: chromosomeToName(chr),
        value: chr,
      }));
      setChromosomeOptionData(optionData);
    }
    setRegions();
  }, [genome, chromosomeRegions]);

  return (
    <div
      className={className}
      {...(testElement != null
        ? getTestProps({component: 'GenomeRegionSelector', element: testElement})
        : {})}
    >
      <SelectDropdown
        displayName={value ? chromosomeToName(value) : 'Choose...'}
        menu={
          <SearchableMenu
            width={MenuWidth.FULL}
            height={MenuHeight.MEDIUM}
            onSelect={onChange}
            optionDatas={chromosomeOptionData}
            selectedValue={value}
          />
        }
      />
    </div>
  );
}

export default GenomeRegionSelector;
